/* eslint-disable no-unused-vars */
/* global G */
import { curry } from 'lib/util'
import asObject from 'lib/sequence/component/children/asObject'
import { actionWithHooks } from 'lib/sequence/module/action'

/**
 * Opens the current or `attribute`'s models `key` as a PDF in a new tab.
 *
 * @param {string} [attribute]                optional sub-model to use
 * @param {boolean} newTab                    whether to open the pdf in a new tab or return the url
 * @param {Gaia.AppModule.Spec} module        module composition
 * @param {Gaia.Component.Spec} component     action component
 * @param {Gaia.AppModule.EventHandler} event triggered event
 * @returns {Promise<*|string>}
 * @private
 */
const _pdf = async (attribute, newTab, module, component, event) => {
  const model = attribute
    ? module[G.MODEL][G.CHILDREN][attribute]
    : module[G.MODEL]
  const { version, api } = model[G.PROPS]
  const key = model[G.STATE][G.REF]

  const url = `/api/v${version}/${api}/${key}/pdf`

  return newTab
    ? await module[G.ADAPTER][G.ROUTER].newTab({ url })
    : url
}

/**
 * PDF As Attachment Event Handler
 *
 * Returns the attachment url returned by {@link _pdf} as an attachment.
 *
 * @type {(function(...[*]): (*))|*}
 */
export const pdfAsAttachment = curry(async (module, component, event) => {
  const model = module[G.MODEL]
  const key = model[G.STATE][G.REF]
  const url = await _pdf(null, false, module, component, event)

  return { url, value: { name: key, type: 'application/pdf' } }
})

/**
 * PDF With Obfuscation Event Handler
 *
 * Returns a URL to the pdf, optionally with obfuscation.
 *
 * @type {(function(...[*]): (*))|*}
 */
export const pdfWithObfuscation = curry(async (module, component, event) => {
  const moduleState = module[G.STATE]
  const model = module[G.MODEL][G.CHILDREN].equipment
  const key = model[G.STATE][G.REF]

  const url = await _pdf('equipment', false, module, component, event)

  const shouldObfuscate = moduleState.obfuscateSerial && moduleState.obfuscateSerial === key

  return `${url}${shouldObfuscate ? '?mask=1' : ''}`
})

/**
 * PDF As Attachment With Obfuscation Event Handler
 *
 * Returns the attachment url returned by {@link pdfWithObfuscation} as an attachment.
 *
 * @type {(function(...[*]): (*))|*}
 */
export const pdfAsAttachmentWithObfuscation = curry(async (module, component, event) => {
  const model = module[G.MODEL][G.CHILDREN].equipment
  const key = model[G.STATE][G.REF]

  const url = await pdfWithObfuscation(module, component, event)

  return { url, value: { name: key, type: 'application/pdf' } }
})

/**
 * Opens the current models `key` as a PDF in a new tab.
 *
 * @type {(function(...[*]): (*))|*}
 */
export const pdfInNewTab = curry(
  async (module, component, event) => await _pdf(null, true, module, component, event),
)

/**
 * Returns the url of the models `key` pdf.
 */
export default curry(
  async (module, component, event) => await _pdf(null, false, module, component, event),
)
